import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Reveal } from "@/components/Reveal";
import { AppDetailModal } from "@/components/AppDetailModal";
import { SmartImage } from "@/components/SmartImage";
import { imgSrc, track } from "@/lib/api";
import { ArrowUpRight, Star, Monitor, Smartphone } from "lucide-react";

export const PlatformPills = ({ platforms, appId }) => {
  if (!platforms?.length) return null;
  return (
    <div className="flex flex-wrap gap-2 mt-2.5">
      {platforms.map((p) => (
        <span
          key={p}
          data-testid={`platform-pill-${appId}-${p.replace(/\s+/g, "-").toLowerCase()}`}
          className="inline-flex items-center gap-1.5 font-mono2 text-[9px] uppercase tracking-widest border border-[#282836] text-[#9E9EB3] rounded-full px-2.5 py-1"
        >
          {p === "Android TV" ? (
            <Monitor className="w-3 h-3 text-[#FF5500]" />
          ) : (
            <Smartphone className="w-3 h-3 text-[#FF5500]" />
          )}
          {p}
        </span>
      ))}
    </div>
  );
};

const CardImage = ({ app, tall }) => (
  <div className={`img-zoom card-glow relative overflow-hidden rounded-2xl border border-[#282836] bg-[#1A1A26] ${tall ? "aspect-video lg:aspect-[21/9]" : "aspect-video"}`}>
    {app.main_image ? (
      <SmartImage src={imgSrc(app.main_image)} alt={`${app.name} screenshot`} phoneFrame={app.phone_frame !== false} />
    ) : (
      <div className="w-full h-full flex items-center justify-center font-mono2 text-xs text-[#63637A] uppercase tracking-widest">
        Screenshot coming soon
      </div>
    )}
    <div className="absolute inset-0 bg-gradient-to-t from-[#08080C]/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
    <div className="absolute bottom-4 right-4 w-10 h-10 rounded-full bg-[#FF5500] text-[#08080C] flex items-center justify-center opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500">
      <ArrowUpRight className="w-5 h-5" />
    </div>
  </div>
);

export const AppGallery = ({ apps, loading }) => {
  const [selected, setSelected] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const featured = apps.find((a) => a.featured);
  const rest = apps.filter((a) => a !== featured);

  const openApp = (app) => {
    setSelected(app);
    track("app_view", app.id);
    setSearchParams(app.slug ? { app: app.slug } : {}, { replace: true });
  };

  const closeApp = () => {
    setSelected(null);
    setSearchParams({}, { replace: true });
  };

  useEffect(() => {
    const slug = searchParams.get("app");
    if (!slug || !apps.length) return;
    const match = apps.find((a) => a.slug === slug || a.id === slug);
    if (match && selected?.id !== match.id) {
      setSelected(match);
      track("app_view", match.id);
      setTimeout(() => document.getElementById("apps")?.scrollIntoView({ behavior: "smooth" }), 150);
    }
  }, [apps, searchParams]);

  return (
    <section id="apps" data-testid="apps-section" className="relative py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <p className="font-mono2 text-xs uppercase tracking-[0.3em] text-[#FF5500] mb-4">The Stable</p>
          <div className="flex flex-wrap items-end justify-between gap-6 mb-16">
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight max-w-xl">
              Apps you know and trust — <span className="text-outline">rebranded with your logo.</span>
            </h2>
            <p className="font-mono2 text-xs uppercase tracking-[0.2em] text-[#63637A]">
              {apps.length} {apps.length === 1 ? "app" : "apps"} available
            </p>
          </div>
        </Reveal>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[0, 1, 2].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="h-6 w-2/3 bg-[#1A1A26] rounded mb-4" />
                <div className="aspect-video bg-[#1A1A26] rounded-2xl" />
              </div>
            ))}
          </div>
        ) : apps.length === 0 ? (
          <p data-testid="apps-empty-message" className="text-[#63637A] text-center py-20 font-mono2 text-sm uppercase tracking-[0.2em]">
            The stable is empty — check back soon.
          </p>
        ) : (
          <>
            {featured && (
              <Reveal>
                <article
                  data-testid={`app-card-${featured.id}`}
                  onClick={() => openApp(featured)}
                  className="group cursor-pointer mb-16"
                >
                  <div className="flex flex-wrap items-baseline justify-between gap-3 mb-4">
                    <div>
                      <div className="flex items-center gap-4">
                        <span className="inline-flex items-center gap-1.5 font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#08080C] bg-[#FF5500] rounded-full px-3 py-1.5">
                          <Star className="w-3 h-3 fill-current" /> Featured
                        </span>
                        <h3 className="font-display text-2xl sm:text-3xl font-semibold group-hover:text-[#FF5500] transition-colors">
                          {featured.name}
                        </h3>
                      </div>
                      <PlatformPills platforms={featured.platforms} appId={featured.id} />
                    </div>
                    <span className="text-sm text-[#9E9EB3]">{featured.tagline}</span>
                  </div>
                  <CardImage app={featured} tall />
                </article>
              </Reveal>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14">
              {rest.map((app, i) => (
                <Reveal key={app.id} delay={(i % 3) * 0.12}>
                  <article
                    data-testid={`app-card-${app.id}`}
                    onClick={() => openApp(app)}
                    className="group cursor-pointer"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="font-display text-xl sm:text-2xl font-semibold group-hover:text-[#FF5500] transition-colors">
                          {app.name}
                        </h3>
                        <PlatformPills platforms={app.platforms} appId={app.id} />
                      </div>
                      <span className="font-mono2 text-xs text-[#63637A] mt-1">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                    </div>
                    <CardImage app={app} />
                    <p className="mt-3 text-sm text-[#9E9EB3]">{app.tagline}</p>
                  </article>
                </Reveal>
              ))}
            </div>
          </>
        )}
      </div>

      {selected && <AppDetailModal app={selected} onClose={closeApp} />}
    </section>
  );
};
